import { chakra } from '@chakra-ui/react';
import { type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useIsCompact } from './useMediaQuery';
import { focusRing } from '../primitives/_shared';

const Frame = chakra('div', {
  base: { display: 'flex', minWidth: 0, width: 'full', alignItems: 'stretch', gap: '5' },
});

const ListPane = chakra('div', {
  base: { minWidth: 0, display: 'flex', flexDirection: 'column' },
});

const DetailPane = chakra('div', {
  base: { flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' },
});

const BackBar = chakra('div', {
  base: { display: 'flex', alignItems: 'center', mb: '3' },
});

const BackButton = chakra('button', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1.5',
    fontFamily: 'body',
    fontSize: 'md',
    fontWeight: 'medium',
    color: 'action.primary',
    bg: 'transparent',
    border: 'none',
    borderRadius: 'md',
    px: '2',
    py: '1',
    ml: '-2',
    cursor: 'pointer',
    _hover: { bg: 'action.secondary' },
    ...focusRing,
  },
});

const EmptyDetail = chakra('div', {
  base: {
    flex: '1 1 0',
    minHeight: '16rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    p: '8',
    borderWidth: '1px',
    borderStyle: 'dashed',
    borderColor: 'border.default',
    borderRadius: 'xl',
    color: 'text.muted',
    fontFamily: 'body',
    fontSize: 'md',
  },
});

export interface SplitViewProps {
  /** The master list pane (left on desktop, full-screen when nothing selected on mobile). */
  list: ReactNode;
  /** The detail pane (right on desktop, full-screen with a back control on mobile). */
  detail: ReactNode;
  /** Whether a detail is currently selected (drives the mobile list↔detail switch + desktop placeholder). */
  hasSelection: boolean;
  /** Called by the mobile back affordance to clear the selection. */
  onBack?: () => void;
  backLabel?: string;
  /** Fixed width of the list pane on desktop. */
  listWidth?: string;
  /** Placeholder shown in the desktop detail pane when nothing is selected. */
  emptyDetail?: ReactNode;
  listLabel?: string;
  detailLabel?: string;
  /**
   * When true (default), the list fills the full width on desktop until a row is
   * selected, then collapses to `listWidth` beside the detail — so browsing gets the
   * whole canvas. When false, the two panes are always shown (with `emptyDetail` as a
   * persistent placeholder), which suits an always-on review inbox.
   */
  expandListWhenEmpty?: boolean;
}

/**
 * A master/detail split view (§ composition — "Runs & Approvals: split view so the
 * monitoring/review loop keeps context"). On desktop it shows a fixed-width list pane
 * beside a flexible detail pane, with a dashed placeholder until a row is selected. On
 * tablet/mobile it collapses to list → detail navigation: the list fills the screen,
 * and selecting an item swaps to a full-screen detail with a back affordance (not two
 * cramped columns).
 */
export function SplitView({
  list,
  detail,
  hasSelection,
  onBack,
  backLabel = 'Back',
  listWidth = '25rem',
  emptyDetail,
  listLabel = 'List',
  detailLabel = 'Detail',
  expandListWhenEmpty = true,
}: SplitViewProps) {
  const compact = useIsCompact();

  if (compact) {
    if (hasSelection) {
      return (
        <chakra.div minW={0}>
          {onBack && (
            <BackBar>
              <BackButton type="button" onClick={onBack} data-testid="split-back">
                <ArrowLeft size={16} strokeWidth={2.5} aria-hidden />
                {backLabel}
              </BackButton>
            </BackBar>
          )}
          <chakra.section aria-label={detailLabel} minW={0}>
            {detail}
          </chakra.section>
        </chakra.div>
      );
    }
    return (
      <chakra.section aria-label={listLabel} minW={0}>
        {list}
      </chakra.section>
    );
  }

  // Desktop, nothing selected, expand-mode → the list owns the full width.
  if (!hasSelection && expandListWhenEmpty) {
    return (
      <chakra.section aria-label={listLabel} minW={0}>
        {list}
      </chakra.section>
    );
  }

  return (
    <Frame>
      <ListPane as="section" aria-label={listLabel} flex={`0 0 ${listWidth}`} maxW={listWidth}>
        {list}
      </ListPane>
      <DetailPane as="section" aria-label={detailLabel}>
        {hasSelection ? detail : (emptyDetail ?? <EmptyDetail>Select an item to see its details.</EmptyDetail>)}
      </DetailPane>
    </Frame>
  );
}
